import React, { useState, useRef, useEffect } from 'react'
import { Sparkles, Plus, X } from 'lucide-react'
import { Button } from 'components/ui/button'
import { AspectRatioSelector } from 'components/ui/aspect-ratio-selector'
import { ModelSelector } from 'components/ui/model-selector'
import { useForm } from 'react-hook-form'
import { useUploadFile } from 'hooks/query/aiStudio/useUploadFile'
import { useFreeSynthesis } from 'hooks/query/aiStudio/useFreeSynthesis'
import { useFreeSynthesisStatus } from 'hooks/query/aiStudio/useFreeSynthesisStatus'
import { FreeSynthesisParams } from 'service/aiStudio'
import useUserStore from 'store/useUserStore'
import { useNavigate } from 'react-router-dom'
import { usePointManager } from 'hooks/usePointManager'
import { PointInsufficientModal } from 'pages/ai-studio/tabs/FashionEditorTab/components/PointInsufficientModal'

interface FormData {
  prompt: string
  images: File[]
}

interface GenerationControlBarProps {
  onStartPolling?: (
    requestId: number,
    prompt: string,
    totalJobs?: number,
    aspectRatio?: string,
  ) => void
  onPollingComplete?: () => void
}

export default function GenerationControlBar({
  onStartPolling,
  onPollingComplete,
}: GenerationControlBarProps = {}) {
  const [selectedModel, setSelectedModel] = useState<string>('nano-banana')
  const [modelCost, setModelCost] = useState<number>(100)
  const [apiModel, setApiModel] = useState<string>('gemini-2.5-flash-image')

  const { currentPoint, checkPointsRealtime } =
    usePointManager({
      requiredPoints: modelCost,
    })
  const [images, setImages] = useState<string[]>([])
  const [imageFiles, setImageFiles] = useState<File[]>([])
  const [currentRequestId, setCurrentRequestId] = useState<number | null>(null)
  const [aspectRatio, setAspectRatio] = useState<string>('9:16')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [isPointInsufficientModalOpen, setIsPointInsufficientModalOpen] =
    useState(false)
  const { userInfo } = useUserStore()
  const navigate = useNavigate()
  const { register, handleSubmit, watch, setValue } = useForm<FormData>({
    defaultValues: {
      prompt: '',
      images: [],
    },
  })

  const uploadFileMutation = useUploadFile()
  const freeSynthesisMutation = useFreeSynthesis()

  // 폴링 훅 - 현재 요청이 있을 때만 활성화
  const { data: statusData } = useFreeSynthesisStatus({
    id: currentRequestId,
    enabled: !!currentRequestId,
  })

  const prompt = watch('prompt')

  // 폴링 상태 변화 감지 - synthesisJobs 배열 확인
  useEffect(() => {
    if (statusData?.synthesisJobs && currentRequestId) {
      const { synthesisJobs } = statusData

      // processing 또는 pending 중인 job이 있는지 확인
      const processingJobs = synthesisJobs.filter(
        (job: any) => job.status === 'processing' || job.status === 'pending',
      )

      // 모든 job이 완료되었으면 (processing/pending이 없으면) 폴링 종료
      if (processingJobs.length === 0 && synthesisJobs.length > 0) {
        setCurrentRequestId(null)
        if (onPollingComplete) {
          onPollingComplete()
        }
      }
    }
  }, [statusData?.synthesisJobs, currentRequestId, onPollingComplete])

  const handleImageUpload = (e: any) => {
    const files: File[] = Array.from(e.target.files)
    const previews = files.map((file) => URL.createObjectURL(file))
    setImages((prev) => [...prev, ...previews])
    setImageFiles((prev) => [...prev, ...files])
    setValue('images', [...imageFiles, ...files])
  }

  const handleRemoveImage = (indexToRemove: number) => {
    setImages((prev) => prev.filter((_, index) => index !== indexToRemove))
    setImageFiles((prev) => prev.filter((_, index) => index !== indexToRemove))
    setValue(
      'images',
      imageFiles.filter((_, index) => index !== indexToRemove),
    )
  }

  const handleModelChange = (model: string, cost: number, apiValue: string) => {
    setSelectedModel(model)
    setModelCost(cost)
    setApiModel(apiValue)
  }

  const handleGenerate = async (data: FormData) => {
    if (!userInfo) {
      navigate('/sign-up')
    }
    const hasEnoughPoints = await checkPointsRealtime()
    if (!hasEnoughPoints) {
      setIsPointInsufficientModalOpen(true)
      return
    }
    try {
      if (!data.prompt.trim()) {
        alert('프롬프트를 입력해주세요.')
        return
      }

      let uploadedImageUrls: string[] = []

      // 이미지가 있는 경우에만 업로드
      if (data.images.length > 0) {
        const uploadPromises = data.images.map((file) =>
          uploadFileMutation.mutateAsync(file),
        )

        const uploadResults = await Promise.all(uploadPromises)
        uploadedImageUrls = uploadResults.map((result) => result.fileUrl)
      }

      // 자유 합성 API 호출
      const freeSynthesisParams: FreeSynthesisParams = {
        uploadedImageUrls,
        userPrompt: data.prompt,
        aspectRatio: aspectRatio,
        model: apiModel,
      }

      const result =
        await freeSynthesisMutation.mutateAsync(freeSynthesisParams)

      // 생성된 요청 ID를 저장하여 폴링 시작
      setCurrentRequestId(result.id)

      // 폴링 시작 알림 (즉시 스켈레톤 표시)
      if (onStartPolling) {
        onStartPolling(
          result.id,
          data.prompt,
          result.totalJobs || 4,
          aspectRatio,
        )
      }
    } catch (error) {
      // 에러 로깅
      alert('이미지 생성 중 오류가 발생했습니다.')
    }
  }

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }

  useEffect(() => {
    adjustTextareaHeight()
  }, [prompt])

  // 폴링 상태가 완료되면 현재 요청 ID 초기화
  useEffect(() => {
    if (
      statusData &&
      (statusData.status === 'completed' || statusData.status === 'failed')
    ) {
      setCurrentRequestId(null)
    }
  }, [statusData])

  const isLoading =
    uploadFileMutation.isPending || freeSynthesisMutation.isPending
  const isGenerating = currentRequestId !== null

  return (
    <form
      onSubmit={handleSubmit(handleGenerate)}
    >
      <div
        className="fixed bottom-0 left-1/2 flex w-full max-w-[1000px] -translate-x-1/2 flex-col gap-4 border border-zinc-700/50 p-6 text-white shadow-xl md:bottom-4 md:rounded-[32px]"
        style={{
          backgroundColor: 'rgba(19, 19, 19, 0.9)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
      >
        {/* 이미지 영역 - 조건부 렌더링 */}
        {images.length > 0 && (
          <div className="flex items-center gap-3 overflow-x-auto pb-2">
            {images.map((src, idx) => (
              <div key={idx} className="group relative shrink-0">
                <img
                  src={src}
                  alt={`preview-${idx}`}
                  className="h-14 w-14 rounded-xl border border-zinc-700/50 object-cover"
                />
                <button
                  onClick={() => handleRemoveImage(idx)}
                  className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-zinc-600 bg-zinc-800 opacity-0 transition-opacity hover:bg-zinc-700 group-hover:opacity-100"
                >
                  <X size={12} className="text-white" />
                </button>
              </div>
            ))}
            <label className="flex h-14 w-14 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-zinc-700/50 bg-zinc-800/50 transition-colors hover:bg-zinc-700/50">
              <Plus size={18} className="text-zinc-400" />
              <input
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
            </label>
          </div>
        )}

        {/* 텍스트 입력창 */}
        <div className="flex max-w-[754px] flex-1 items-start justify-start gap-3 rounded-xl py-3">
          {images.length === 0 && (
            <label className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-xl border border-zinc-700/50 bg-zinc-800/50 transition-colors hover:bg-zinc-700/50">
              <Plus size={18} className="text-zinc-400" />
              <input
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
            </label>
          )}
          <textarea
            {...register('prompt', { required: '프롬프트를 입력해주세요' })}
            ref={textareaRef}
            placeholder="원하는 이미지를 설명해 주세요..."
            className="w-full resize-none overflow-hidden bg-transparent text-sm text-zinc-100 placeholder:text-zinc-400 focus:outline-none"
            style={{ minHeight: '40px' }}
            rows={1}
            onChange={(e) => {
              setValue('prompt', e.target.value)
              adjustTextareaHeight()
            }}
          />
        </div>

        {/* 버튼 영역 */}
        <div className="flex h-10 items-center justify-between">
          <div className="flex items-center gap-3">
            <ModelSelector
              value={selectedModel}
              onChange={handleModelChange}
            />
            <AspectRatioSelector
              value={aspectRatio}
              onChange={setAspectRatio}
            />
          </div>

          <Button
            type="submit"
            disabled={isLoading || isGenerating}
            className="flex h-16 items-center gap-2 rounded-xl border-0 bg-main px-6 py-2 font-semibold text-white hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Sparkles size={16} />
            {isLoading ? (
              '업로드 중...'
            ) : isGenerating ? (
              '생성 중...'
            ) : (
              <>
                <span>생성하기</span>
                <span className="flex items-center gap-[2px]">
                  <img
                    src="/assets/img/cash.png"
                    alt="check_circle"
                    className="h-[13px] w-[11px]"
                  />
                  {modelCost}
                </span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* 포인트 부족 모달 */}
      <PointInsufficientModal
        isOpen={isPointInsufficientModalOpen}
        onClose={() => setIsPointInsufficientModalOpen(false)}
        currentPoint={currentPoint}
        requiredPoint={modelCost}
      />
    </form>
  )
}
